import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetCompanyDetailAndJobGroups } from 'ogp/hooks/queryHooks/companies/use-get-company-detail-and-job-groups';
import { useGetOGPUserInfo } from 'ogp/hooks/queryHooks/users/use-get-ogp-user-info';
import { isLimitedOgpCompany } from 'ogp/limited-company-capabilities/company-type';
import { invariant } from 'shared/utils/utils';
import { OgpCompanyTypeContext } from './ogp-company-type-provider.types';
import { resolveNavigatedOgpCompanyType } from './resolve-navigated-ogp-company-type';

// eslint-disable-next-line no-restricted-properties
const OgpCompanyTypeReactContext = React.createContext<OgpCompanyTypeContext | undefined>(
  undefined
);

const useOgpCompanyTypeContext = () => {
  // eslint-disable-next-line no-restricted-properties
  const context = React.useContext(OgpCompanyTypeReactContext);
  invariant(context, 'OgpCompanyTypeContext can only be accessed within OgpCompanyTypeProvider');
  return context;
};

const useOptionalOgpCompanyTypeContext = () => {
  // eslint-disable-next-line no-restricted-properties
  return React.useContext(OgpCompanyTypeReactContext);
};

const OgpCompanyTypeProvider = ({ children }: React.PropsWithChildren<unknown>) => {
  const { companyHash } = useParams();
  const userQuery = useGetOGPUserInfo();
  const companyQuery = useGetCompanyDetailAndJobGroups();

  const value = useMemo((): OgpCompanyTypeContext => {
    const userCompany = userQuery.data?.userInfo.company;
    const companyType = resolveNavigatedOgpCompanyType({
      navigatedCompanyHash: companyHash,
      userCompanySlug: userCompany?.slug,
      userCompanyType: userCompany?.companyType,
      navigatedCompanyType: companyQuery.data?.company.companyType,
      isNavigatedCompanyDetailSettled: companyQuery.status === 'success',
    });

    const hasAuthoritativeCompanyType =
      companyQuery.status === 'success' ||
      Boolean(companyHash && userCompany?.slug === companyHash && userCompany.companyType);

    return {
      companyHash,
      companyType,
      isLimitedCompany: isLimitedOgpCompany(companyType),
      isLoading: companyQuery.isLoading && !hasAuthoritativeCompanyType,
    };
  }, [
    companyHash,
    companyQuery.data?.company.companyType,
    companyQuery.isLoading,
    companyQuery.status,
    userQuery.data?.userInfo.company,
  ]);

  return (
    <OgpCompanyTypeReactContext.Provider value={value}>
      {children}
    </OgpCompanyTypeReactContext.Provider>
  );
};

export { OgpCompanyTypeProvider, useOgpCompanyTypeContext, useOptionalOgpCompanyTypeContext };
